import React, { useCallback, useEffect, useRef, useState } from "react";
import { Building2, Plus, Search, Pencil, AlertTriangle, MapPin, Route } from "lucide-react";
import { toast } from "sonner";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import MapPicker from "../../components/MapPicker";

const PAGE_SIZE = 20;

const BLANK = {
  name: "", address: "", city: "", pincode: "",
  lat: "", lng: "", notes: "", is_active: true, route_id: "",
};

/**
 * Serviceable Apartments — tenant admin management.
 *
 * A tenant delivering into gated communities registers each complex once
 * here; customers then pick it from a searchable list and supply only a flat
 * number. The feature is opt-in per tenant (Settings → Operations), and this
 * page stays reachable either way so an admin can build the list BEFORE
 * switching it on for customers.
 */
export default function AdminApartments() {
  const { tenant } = useAuth();
  const primary = tenant?.branding?.primary_color || "#3A86FF";
  const apartmentsOn = tenant?.serviceable_apartments_enabled === true;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [editing, setEditing] = useState(null);   // null | {} (new) | row (edit)
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState("");
  // Delivery routes the apartment can be put on. Apartment → route → the
  // route's per-slot rider (Routes & Riders) — the apartment never stores a
  // rider itself. Supervisors can read routes, so this works for both roles.
  const [routes, setRoutes] = useState([]);
  useEffect(() => {
    api.get("/api/admin/routes")
      .then(({ data }) => setRoutes((data.routes || []).filter((r) => r.is_active !== false)))
      .catch(() => setRoutes([]));
  }, []);
  // What the map last auto-filled, per field. Lets us tell "this value came
  // from the map" (safe to refresh when the pin moves) from "the admin typed
  // this" (never overwrite). Without it, nudging the pin would silently wipe
  // a corrected address.
  const autoFilled = useRef({});
  const [lastResolved, setLastResolved] = useState(null);

  const applyResolved = useCallback((details, { force = false } = {}) => {
    if (!details) return;
    setLastResolved(details);
    setForm((f) => {
      const next = { ...f };
      for (const key of ["address", "city", "pincode"]) {
        const incoming = (details[key] || "").trim();
        if (!incoming) continue;            // map didn't know — keep what's there
        const current = (f[key] || "").trim();
        const isMapValue = current === (autoFilled.current[key] || "").trim();
        // Fill when empty, refresh our own previous auto-fill, or when the
        // admin explicitly asked to re-fill. Never clobber a typed value.
        if (force || !current || isMapValue) {
          next[key] = incoming;
          autoFilled.current[key] = incoming;
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (targetPage = 1) => {
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      if (debounced) params.set("q", debounced);
      params.set("page", String(targetPage));
      params.set("page_size", String(PAGE_SIZE));
      const { data } = await api.get(`/api/admin/apartments?${params}`);
      setRows(data.apartments || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) {
      setLoadError(friendlyMessage(e));
    }
  }, [debounced]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      setLoading(true);
      load(1).finally(() => setLoading(false));
      return;
    }
    setPage(1);
    load(1);
  }, [load]);

  const openNew = () => {
    setForm(BLANK);
    autoFilled.current = {};
    setLastResolved(null);
    setEditing({});
  };
  const openEdit = (r) => {
    setForm({
      name: r.name || "", address: r.address || "", city: r.city || "",
      pincode: r.pincode || "",
      lat: r.lat != null ? String(r.lat) : "",
      lng: r.lng != null ? String(r.lng) : "",
      notes: r.notes || "", is_active: r.is_active !== false,
      route_id: r.route_id || "",
    });
    // Everything on an existing apartment counts as admin-authored — the map
    // may refine it, but must not silently rewrite a saved address.
    autoFilled.current = {};
    setLastResolved(null);
    setEditing(r);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Apartment name is required"); return; }
    // Coordinates are optional, but a half-pair silently breaks the delivery
    // radius check for everyone in the block — catch it before the round-trip.
    const hasLat = String(form.lat).trim() !== "";
    const hasLng = String(form.lng).trim() !== "";
    if (hasLat !== hasLng) { toast.error("Enter both latitude and longitude, or neither"); return; }

    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      pincode: form.pincode.trim(),
      notes: form.notes.trim(),
      is_active: form.is_active,
      // null (not "") clears the apartment's route on the server.
      route_id: form.route_id || null,
    };
    if (hasLat && hasLng) {
      payload.lat = Number(form.lat);
      payload.lng = Number(form.lng);
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.patch(`/api/admin/apartments/${editing.id}`, payload);
        toast.success("Apartment updated");
      } else {
        await api.post("/api/admin/apartments", payload);
        toast.success("Apartment added");
      }
      setEditing(null);
      await load(page);
    } catch (e) { toast.error(friendlyMessage(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (r) => {
    setTogglingId(r.id);
    try {
      await api.patch(`/api/admin/apartments/${r.id}`, { is_active: !r.is_active });
      // Disabling only hides it from NEW customer selections — say so, because
      // "disable" reads like it would break existing deliveries.
      toast.success(r.is_active
        ? "Disabled — existing addresses keep delivering"
        : "Apartment enabled");
      await load(page);
    } catch (e) { toast.error(friendlyMessage(e)); }
    finally { setTogglingId(""); }
  };

  const goToPage = (p) => { setPage(p); load(p); };

  if (loading) return <LoadingState fullHeight />;

  return (
    <div className="space-y-5" data-testid="admin-apartments-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight flex items-center gap-2">
            <Building2 className="w-6 h-6" style={{ color: primary }}/> Serviceable Apartments
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Register each apartment complex once. Customers pick it from a list and enter only their flat number.
          </p>
        </div>
        <button onClick={openNew}
                className="h-10 px-4 rounded-2xl text-white text-sm font-bold inline-flex items-center gap-1.5"
                style={{ background: primary }}
                data-testid="add-apartment-btn">
          <Plus className="w-4 h-4"/> Add apartment
        </button>
      </div>

      {/* The list is editable regardless, so an admin can build it up before
          exposing it to customers — but they need to know it isn't live yet. */}
      {!apartmentsOn && (
        <div className="rounded-2xl border border-brand-sand/40 bg-brand-sand/10 px-4 py-3 text-sm"
             data-testid="apartments-disabled-notice">
          Serviceable Apartments is currently <b>off</b> for this store, so customers still
          enter addresses manually. Turn it on in <b>Settings → Operations</b> once this list is ready.
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400"/>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search by name, address, city or pincode…"
               className="w-full h-11 pl-10 pr-4 rounded-2xl border border-neutral-200 text-sm"
               data-testid="apartment-search"/>
      </div>

      {loadError ? (
        <EmptyState icon={AlertTriangle} title="Couldn't load apartments" description={loadError}
          action={<button onClick={() => load(page)} className="h-10 px-4 rounded-2xl text-white text-sm font-bold" style={{ background: primary }}>Retry</button>}
          testId="apartments-load-error"/>
      ) : rows.length === 0 ? (
        <EmptyState icon={Building2}
          title={debounced ? "No apartments match that search" : "No apartments yet"}
          description={debounced
            ? "Try a different name, city or pincode."
            : "Add the first apartment complex you deliver to. Customers will then be able to pick it instead of typing a full address."}
          testId="apartments-empty"/>
      ) : (
        <>
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id}
                   className={`bg-white rounded-2xl border p-4 flex items-start gap-3 ${r.is_active ? "border-neutral-200" : "border-neutral-200 opacity-70"}`}
                   data-testid={`apartment-row-${r.id}`}>
                <div className="w-10 h-10 rounded-2xl bg-brand-cream flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-neutral-500"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm flex items-center gap-2 flex-wrap">
                    {r.name}
                    {!r.is_active && (
                      <span className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500 text-[9px] font-extrabold uppercase tracking-wider"
                            data-testid={`apartment-inactive-${r.id}`}>Inactive</span>
                    )}
                  </div>
                  {r.address && <div className="text-xs text-neutral-500 mt-0.5 break-words">{r.address}</div>}
                  {r.route_id && (
                    <div className="text-xs text-brand-sky font-bold mt-0.5 inline-flex items-center gap-1"
                         data-testid={`apartment-route-${r.id}`}>
                      <Route className="w-3 h-3"/> {r.route_name || "Route"}
                    </div>
                  )}
                  <div className="text-xs text-neutral-400 mt-0.5 flex items-center gap-2 flex-wrap">
                    {[r.city, r.pincode].filter(Boolean).join(" · ")}
                    {r.lat != null && r.lng != null && (
                      <span className="inline-flex items-center gap-1" data-testid={`apartment-geo-${r.id}`}>
                        <MapPin className="w-3 h-3"/> {Number(r.lat).toFixed(4)}, {Number(r.lng).toFixed(4)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch checked={r.is_active !== false}
                          disabled={togglingId === r.id}
                          onCheckedChange={() => toggleActive(r)}
                          data-testid={`apartment-toggle-${r.id}`}/>
                  <button onClick={() => openEdit(r)}
                          className="w-9 h-9 rounded-xl border border-neutral-200 inline-flex items-center justify-center"
                          data-testid={`apartment-edit-${r.id}`}>
                    <Pencil className="w-4 h-4"/>
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} total={total}
                      pageSize={PAGE_SIZE} onPageChange={goToPage}/>
        </>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {/* Column layout so the footer can be pinned: header and footer keep
            their natural height, the field list takes the rest and scrolls.
            `flex` overrides the base `grid`, and p-0 moves padding onto the
            sections so the scroll area meets the footer edge-to-edge. */}
        <DialogContent
          className="flex flex-col gap-0 p-0 overflow-hidden max-w-lg sm:rounded-2xl"
          data-testid="apartment-dialog">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
            <DialogTitle>{editing?.id ? "Edit apartment" : "Add apartment"}</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">
              The address and location you set here are applied to every customer address that selects this apartment.
            </DialogDescription>
          </DialogHeader>
          {/* min-h-0 is required: a flex child defaults to min-height:auto,
              which refuses to shrink below its content — the container would
              grow instead of scrolling, and the footer would be pushed off
              screen again. */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4 space-y-3">
            {/* Location first: searching the map fills address / city /
                pincode / coordinates below, so the admin only types the one
                thing a map can't know — the building's name. */}
            <MapPicker
              lat={form.lat === "" ? null : form.lat}
              lng={form.lng === "" ? null : form.lng}
              onChange={(la, ln) => setForm((f) => ({
                ...f, lat: String(la.toFixed(6)), lng: String(ln.toFixed(6)),
              }))}
              onResolve={applyResolved}
              label="Find the apartment on the map"
              hint="Search the apartment, then drag the pin to the gate."
              testIdPrefix="apartment-map"
            />
            {lastResolved?.display && (
              <div className="text-[11px] text-neutral-500 -mt-1 break-words" data-testid="apartment-resolved-place">
                Found: {lastResolved.display}
              </div>
            )}

            <Field label="Apartment name *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Green Valley Apartments" data-testid="apartment-name"/>
              <p className="text-[11px] text-neutral-500 mt-1">
                A map can&rsquo;t reliably name a building — enter it yourself.
              </p>
            </Field>
            <Field label="Address">
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                     placeholder="Road 12, Banjara Hills" data-testid="apartment-address"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City">
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                       placeholder="Hyderabad" data-testid="apartment-city"/>
              </Field>
              <Field label="Pincode">
                <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                       placeholder="500034" data-testid="apartment-pincode"/>
              </Field>
            </div>
            {/* The stored coordinates. Driven by the map above, but kept
                editable — pasting exact coordinates from elsewhere is still
                the fastest path for some admins, and editing them recenters
                the map (LeafletMap is controlled on lat/lng). */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitude">
                <Input type="number" step="any" value={form.lat}
                       onChange={(e) => setForm({ ...form, lat: e.target.value })}
                       placeholder="17.4126" data-testid="apartment-lat"/>
              </Field>
              <Field label="Longitude">
                <Input type="number" step="any" value={form.lng}
                       onChange={(e) => setForm({ ...form, lng: e.target.value })}
                       placeholder="78.4482" data-testid="apartment-lng"/>
              </Field>
            </div>
            <div className="flex items-center justify-between gap-2 -mt-1">
              <p className="text-[11px] text-neutral-500">
                Optional. When set, the delivery-radius check uses this gate location for every flat in the block.
              </p>
              <span className="flex items-center gap-3 shrink-0">
                {/* Typed values are protected from the pin moving; this is the
                    explicit way to overwrite them with what the map found. */}
                {lastResolved && (
                  <button type="button"
                          onClick={() => applyResolved(lastResolved, { force: true })}
                          className="text-[11px] font-bold text-brand-sky underline"
                          data-testid="apartment-refill-from-map">
                    Re-fill from map
                  </button>
                )}
                {(form.lat !== "" || form.lng !== "") && (
                  <button type="button"
                          onClick={() => setForm({ ...form, lat: "", lng: "" })}
                          className="text-[11px] font-bold text-brand-clay underline"
                          data-testid="apartment-clear-location">
                    Clear
                  </button>
                )}
              </span>
            </div>
            <Field label="Delivery route">
              <select value={form.route_id} onChange={(e) => setForm({ ...form, route_id: e.target.value })}
                      className="h-11 px-3 rounded-2xl border border-neutral-200 bg-white w-full text-sm"
                      data-testid="apartment-route">
                <option value="">No route — use each customer's own route</option>
                {routes.map((rt) => (
                  <option key={rt.id} value={rt.id}>{rt.name}</option>
                ))}
              </select>
              <p className="text-xs text-neutral-500 mt-1">
                Orders from this apartment go to this route&apos;s rider for each slot (set in Routes &amp; Riders),
                ahead of the customer&apos;s own route. Open upcoming orders move when you change it.
              </p>
            </Field>
            <Field label="Internal notes">
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                     placeholder="Gate 2 entry, security desk" data-testid="apartment-notes"/>
            </Field>
            <label className="flex items-center gap-3 pt-1">
              <Switch checked={form.is_active}
                      onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                      data-testid="apartment-active"/>
              <span className="text-sm font-bold">Active</span>
              <span className="text-xs text-neutral-500">Inactive apartments can't be picked for new addresses.</span>
            </label>
          </div>
          {/* Pinned, not scrolled to: Save stays reachable no matter how long
              the form is or how much of the screen the keyboard covers.
              pb-safe-4 keeps the padding AND clears the iOS home indicator. */}
          <div className="shrink-0 flex justify-end gap-2 px-6 pt-4 pb-safe-4 border-t border-neutral-200 bg-background rounded-b-2xl">
            <button onClick={() => setEditing(null)}
                    className="h-10 px-4 rounded-2xl border border-neutral-200 text-sm font-bold">Cancel</button>
            <button onClick={save} disabled={saving}
                    className="h-10 px-5 rounded-2xl text-white text-sm font-bold disabled:opacity-50"
                    style={{ background: primary }}
                    data-testid="apartment-save">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
